import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  Paper,
  Stack,
  Divider,
  LinearProgress,
  Chip,
  Avatar
} from '@mui/material';
import Loading from './Loading';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { dashboardApi } from '../services/api';
import { DashboardStats } from '../types';
import { authService } from '../services/auth';
import { useMultilingual } from '../hooks/useMultilingual';
import Notifications from './Notifications';

const STATUS_COLORS: Record<string, string> = {
  pending: '#E67E22',
  processing: '#2980B9',
  shipped: '#16A085',
  inTransit: '#1ABC9C',
  outForDelivery: '#27AE60',
  delivered: '#2C3E50',
  failed: '#C0392B',
  cancelled: '#7F8C8D',
  returned: '#8E44AD'
};

const COUNTRY_PALETTE = ['#FF6500', '#2C3E50', '#16A085', '#2980B9', '#8E44AD', '#E67E22', '#1ABC9C', '#C0392B', '#7F8C8D', '#27AE60'];

const SectionTitle: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="body2" color="text.secondary">
        {subtitle}
      </Typography>
    )}
  </Box>
);

const RankedList: React.FC<{
  items: { id: string; label: string; sublabel?: string; count: number; avatar?: string }[];
  max: number;
  emptyText: string;
  barColor: string;
}> = ({ items, max, emptyText, barColor }) => {
  if (items.length === 0) {
    return <Alert severity="info">{emptyText}</Alert>;
  }

  return (
    <Stack spacing={1.5} sx={{ maxHeight: 360, overflowY: 'auto', pr: 0.5 }}>
      {items.map((item, index) => {
        const pct = max > 0 ? Math.round((item.count / max) * 100) : 0;
        return (
          <Box key={item.id}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
              <Typography
                variant="caption"
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  bgcolor: 'action.hover',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  flexShrink: 0
                }}
              >
                {index + 1}
              </Typography>
              {item.avatar ? (
                <Avatar src={item.avatar} alt={item.label} sx={{ width: 28, height: 28 }} />
              ) : null}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {item.label}
                </Typography>
                {item.sublabel && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {item.sublabel}
                  </Typography>
                )}
              </Box>
              <Chip
                size="small"
                label={item.count.toLocaleString()}
                sx={{ fontWeight: 700, bgcolor: 'action.selected' }}
              />
            </Stack>
            <LinearProgress
              variant="determinate"
              value={pct}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: 'action.hover',
                '& .MuiLinearProgress-bar': { bgcolor: barColor, borderRadius: 3 }
              }}
            />
          </Box>
        );
      })}
    </Stack>
  );
};

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const user = authService.getUser();
  const isAdmin = authService.hasRole('Admin');
  const { getText, isRTL } = useMultilingual();

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError('');
        const statsData = await dashboardApi.getStats();
        setStats(statsData);
      } catch (err) {
        if (err instanceof Error && (err.message.includes('401') || err.message.includes('Unauthorized'))) {
          setError(getText('Your session has expired. Please log in again.', 'انتهت صلاحية الجلسة. يرجى تسجيل الدخول مرة أخرى.'));
        } else {
          setError(getText('Failed to load dashboard data. Please try again.', 'فشل تحميل بيانات لوحة التحكم. يرجى المحاولة مرة أخرى.'));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const orderStatusData = useMemo(() => {
    if (!stats) return [];
    const rows = [
      { key: 'pending', name: getText('Pending', 'معلق'), value: stats.pendingOrders },
      { key: 'processing', name: getText('Processing', 'قيد المعالجة'), value: stats.processingOrders ?? 0 },
      { key: 'shipped', name: getText('Shipped', 'تم الشحن'), value: stats.shippedOrders ?? 0 },
      { key: 'inTransit', name: getText('In transit', 'في الطريق'), value: stats.inTransitOrders ?? 0 },
      { key: 'outForDelivery', name: getText('Out for delivery', 'خرج للتسليم'), value: stats.outForDeliveryOrders ?? 0 },
      { key: 'delivered', name: getText('Delivered', 'تم التسليم'), value: stats.deliveredOrders ?? 0 },
      { key: 'failed', name: getText('Failed', 'فشل'), value: stats.failedOrders ?? 0 },
      { key: 'cancelled', name: getText('Cancelled', 'ملغى'), value: stats.cancelledOrders ?? 0 },
      { key: 'returned', name: getText('Returned', 'معاد'), value: stats.returnedOrders ?? 0 }
    ];
    return rows
      .filter((r) => r.value > 0)
      .map((r) => ({ ...r, color: STATUS_COLORS[r.key] || '#95A5A6' }));
  }, [stats, getText]);

  const monthlyRevenueData = useMemo(() => {
    if (!stats?.monthlyRevenue?.length) return [];
    return stats.monthlyRevenue.map((m) => ({
      month: m.label,
      revenue: Number(m.revenue) || 0,
      orders: m.orderCount
    }));
  }, [stats]);

  const topCategoriesData = useMemo(() => {
    if (!stats?.topCategories?.length) return [];
    return stats.topCategories
      .filter((c) => c.partsCount > 0)
      .map((c) => ({
        name: c.name,
        parts: c.partsCount
      }));
  }, [stats]);

  const countryChartData = useMemo(() => {
    if (!stats?.partsByCountry?.length) return [];
    return stats.partsByCountry
      .filter((c) => c.partsCount > 0)
      .slice(0, 10)
      .map((c, i) => ({
        key: String(c.id ?? `u-${i}`),
        name: isRTL && c.nameAr ? c.nameAr : c.name,
        code: c.countryCode,
        parts: c.partsCount,
        color: COUNTRY_PALETTE[i % COUNTRY_PALETTE.length]
      }));
  }, [stats, isRTL]);

  const brandList = useMemo(() => {
    if (!stats?.partsByBrand?.length) return [];
    return stats.partsByBrand
      .filter((b) => b.partsCount > 0)
      .slice(0, 12)
      .map((b) => ({
        id: String(b.id),
        label: b.name,
        sublabel: b.countryName || undefined,
        count: b.partsCount,
        avatar: b.logoUrl
      }));
  }, [stats]);

  const countryList = useMemo(() => {
    if (!stats?.partsByCountry?.length) return [];
    return stats.partsByCountry
      .filter((c) => c.partsCount > 0)
      .map((c, i) => ({
        id: String(c.id ?? `u-${i}`),
        label: isRTL && c.nameAr ? c.nameAr : c.name,
        sublabel: c.countryCode || undefined,
        count: c.partsCount
      }));
  }, [stats, isRTL]);

  if (loading) {
    return (
      <Loading
        variant="gears"
        size={120}
        message={getText('loadingDashboard', 'Loading dashboard data...')}
        fullScreen={false}
      />
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!stats) {
    return (
      <Alert severity="warning">
        {getText('No dashboard data available.', 'لا توجد بيانات لوحة التحكم متاحة.')}
      </Alert>
    );
  }

  const maxCountry = Math.max(...countryList.map((c) => c.count), 1);
  const maxBrand = Math.max(...brandList.map((b) => b.count), 1);

  const metricCards = [
    { label: getText('Active parts', 'قطع نشطة'), value: stats.totalParts, hint: getText('In catalog', 'في الكتالوج') },
    { label: getText('Brands', 'العلامات'), value: stats.totalBrands ?? 0, hint: getText('Active brands', 'علامات نشطة') },
    { label: getText('Countries', 'الدول'), value: stats.totalCountries ?? 0, hint: getText('Countries of origin', 'دول المنشأ') },
    {
      label: getText('Delivered revenue', 'إيرادات المسلّم'),
      value: `$${Number(stats.totalRevenue).toLocaleString()}`,
      hint: getText('Delivered orders only', 'الطلبات المسلّمة فقط')
    },
    {
      label: getText('Low stock', 'مخزون منخفض'),
      value: stats.lowStockParts,
      hint: getText('Needs restock', 'يحتاج إعادة تموين'),
      alert: stats.lowStockParts > 0
    },
    {
      label: getText('Pending orders', 'طلبات معلقة'),
      value: stats.pendingOrders,
      hint: getText('Awaiting action', 'بانتظار إجراء'),
      alert: stats.pendingOrders > 0
    }
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1440, mx: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3 },
          mb: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(115deg, #141414 0%, #2a2a2a 50%, #FF6500 145%)',
          color: '#fff',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'radial-gradient(circle at 85% 20%, rgba(255,101,0,0.35), transparent 45%)',
            pointerEvents: 'none'
          }}
        />
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          spacing={2}
          alignItems={{ sm: 'center' }}
          sx={{ position: 'relative' }}
        >
          <Box>
            <Typography variant="overline" sx={{ opacity: 0.75, letterSpacing: 1.5 }}>
              {getText('Operations overview', 'نظرة عامة على العمليات')}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.75 }}>
              {getText('EDL Auto Parts', 'إي دي إل لقطع الغيار')}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.92, maxWidth: 560 }}>
              {getText('Hello', 'مرحباً')} {user?.firstName || getText('Admin', 'المدير')} —{' '}
              {getText(
                'Catalog mix by country and brand, plus live order health.',
                'توزيع الكتالوج حسب الدولة والعلامة، مع حالة الطلبات الحية.'
              )}
            </Typography>
          </Box>
          {isAdmin && <Notifications />}
        </Stack>
      </Paper>

      <SectionTitle
        title={getText('At a glance', 'لمحة سريعة')}
        subtitle={getText('Real counts from your database.', 'أرقام حقيقية من قاعدة البيانات.')}
      />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' },
          gap: 2,
          mb: 4
        }}
      >
        {metricCards.map((card) => (
          <Card
            key={card.label}
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: card.alert ? 'warning.main' : 'divider',
              borderRadius: 2,
              height: '100%',
              transition: 'border-color 0.2s, transform 0.2s',
              '&:hover': { borderColor: 'primary.main', transform: 'translateY(-2px)' }
            }}
          >
            <CardContent sx={{ py: 2.25, '&:last-child': { pb: 2.25 } }}>
              <Typography variant="h4" sx={{ fontWeight: 800, color: card.alert ? 'warning.dark' : 'text.primary', lineHeight: 1.1 }}>
                {typeof card.value === 'number' ? card.value.toLocaleString() : card.value}
              </Typography>
              <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 700 }}>
                {card.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {card.hint}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      <SectionTitle
        title={getText('Catalog: countries & brands', 'الكتالوج: الدول والعلامات')}
        subtitle={getText(
          'How many active parts belong to each country of origin and each brand.',
          'كم قطعة نشطة تتبع كل دولة منشأ وكل علامة تجارية.'
        )}
      />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 4 }}>
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                {getText('Parts by country', 'القطع حسب الدولة')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {countryList.length} {getText('entries', 'سجل')}
              </Typography>
            </Stack>
            {countryChartData.length === 0 ? (
              <Alert severity="info">{getText('No country-linked parts yet.', 'لا توجد قطع مرتبطة بدولة بعد.')}</Alert>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={countryChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={55} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip
                      formatter={(value: number) => [value.toLocaleString(), getText('Parts', 'قطع')]}
                      labelFormatter={(label, payload) => {
                        const code = payload?.[0]?.payload?.code;
                        return code ? `${label} (${code})` : String(label);
                      }}
                    />
                    <Bar dataKey="parts" radius={[4, 4, 0, 0]}>
                      {countryChartData.map((entry) => (
                        <Cell key={entry.key} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <Divider sx={{ my: 2 }} />
                <RankedList
                  items={countryList}
                  max={maxCountry}
                  emptyText={getText('No country data.', 'لا بيانات دول.')}
                  barColor="#FF6500"
                />
              </>
            )}
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                {getText('Parts by brand', 'القطع حسب العلامة')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {getText('Top', 'الأعلى')} {Math.min(12, brandList.length)}
              </Typography>
            </Stack>
            {brandList.length === 0 ? (
              <Alert severity="info">{getText('No brands with parts yet.', 'لا توجد علامات بقطع بعد.')}</Alert>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart
                    data={brandList.slice(0, 8).map((b) => ({ name: b.label, parts: b.count }))}
                    layout="vertical"
                    margin={{ top: 8, right: 16, left: 8, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                    <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(value: number) => [value.toLocaleString(), getText('Parts', 'قطع')]} />
                    <Bar dataKey="parts" fill="#2C3E50" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <Divider sx={{ my: 2 }} />
                <RankedList
                  items={brandList}
                  max={maxBrand}
                  emptyText={getText('No brand data.', 'لا بيانات علامات.')}
                  barColor="#2C3E50"
                />
              </>
            )}
          </CardContent>
        </Card>
      </Box>

      <SectionTitle
        title={getText('Orders & categories', 'الطلبات والفئات')}
        subtitle={getText('Revenue trend, status mix, and strongest categories.', 'اتجاه الإيرادات، وتوزيع الحالات، وأقوى الفئات.')}
      />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' }, gap: 2, mb: 2 }}>
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
              {getText('Delivered revenue (6 months)', 'إيرادات المسلّم (6 أشهر)')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {getText('Only delivered orders, by order date.', 'الطلبات المسلّمة فقط، حسب تاريخ الطلب.')}
            </Typography>
            {monthlyRevenueData.length === 0 || monthlyRevenueData.every((m) => m.revenue === 0) ? (
              <Alert severity="info">
                {getText('No delivered revenue in the last 6 months yet.', 'لا توجد إيرادات مسلّمة خلال آخر 6 أشهر بعد.')}
              </Alert>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={monthlyRevenueData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(value: number, name: string) =>
                      name === 'revenue'
                        ? [`$${Number(value).toLocaleString()}`, getText('Revenue', 'الإيرادات')]
                        : [value, getText('Orders', 'الطلبات')]
                    }
                  />
                  <Bar dataKey="revenue" fill="#FF6500" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
              {getText('Orders by status', 'الطلبات حسب الحالة')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {getText('Actual order counts from the database.', 'أعداد الطلبات الفعلية من قاعدة البيانات.')}
            </Typography>
            {orderStatusData.length === 0 ? (
              <Alert severity="info">{getText('No orders yet.', 'لا توجد طلبات بعد.')}</Alert>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={orderStatusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={48}
                      outerRadius={78}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {orderStatusData.map((entry) => (
                        <Cell key={entry.key} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: number) => [value, getText('Orders', 'الطلبات')]} />
                  </PieChart>
                </ResponsiveContainer>
                <Divider sx={{ my: 1.5 }} />
                <Stack spacing={0.75}>
                  {orderStatusData.map((row) => (
                    <Stack key={row.key} direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: row.color }} />
                        <Typography variant="body2">{row.name}</Typography>
                      </Stack>
                      <Typography variant="body2" fontWeight={700}>
                        {row.value.toLocaleString()}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </>
            )}
          </CardContent>
        </Card>
      </Box>

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
            {getText('Top categories by parts', 'أعلى الفئات بعدد القطع')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {getText('Active parts currently linked to each category.', 'القطع النشطة المرتبطة بكل فئة.')}
          </Typography>
          {topCategoriesData.length === 0 ? (
            <Alert severity="info">{getText('No categories with parts yet.', 'لا توجد فئات بقطع بعد.')}</Alert>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topCategoriesData} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} />
                <YAxis dataKey="name" type="category" width={140} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="parts" fill="#2C3E50" radius={[0, 4, 4, 0]} name={getText('Parts', 'قطع')} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Dashboard;
